import React, { useState, useEffect } from 'react';
import { Users, Star } from 'lucide-react';
import { mockData } from '../mock';
import EventAlbums from '../components/EventAlbums';
import DishGallery from '../components/DishGallery';

// =========================================================================
// UTILITAIRES YOUTUBE
// =========================================================================
const getYouTubeId = (url) => {
  if (!url) return null;
  // Regex complète incluant les SHORTS
  const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=|\/shorts\/))([\w-]{11})/);
  return match ? match[1] : null;
};

const getYouTubeThumbnail = (url) => {
  const id = getYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : null;
};

// =========================================================================
// 1. GALERIE PLATS
// =========================================================================
const importGalleryImages = () => {
  try {
    const context = require.context('../assets/galerie_creations', false, /\.(png|jpe?g|svg|webp|PNG|JPE?G|SVG|WEBP)$/i);
    return context.keys().map((fileName, index) => {
      const cleanName = fileName.replace('./', '').replace(/\.[^/.]+$/, "");
      return {
        id: index,
        name: cleanName,
        image: context(fileName)
      };
    });
  } catch (err) {
    console.warn("Dossier 'assets/galerie_creations' introuvable.", err);
    return [];
  }
};

// =========================================================================
// 2. SYSTÈME ÉVÉNEMENTS
// =========================================================================
const useEventLoader = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const fileContext = require.context('../assets/events', true, /\.(png|jpe?g|svg|webp|mp4|webm|mov|txt)$/i);
        const folders = {};

        fileContext.keys().forEach(filePath => {
          const parts = filePath.split('/');
          if (parts.length < 3) return; 

          const folderName = parts[1]; 
          const fileName = parts[2];   
          const fileUrl = fileContext(filePath);

          if (!folders[folderName]) {
            folders[folderName] = {
              name: folderName,
              mediaFiles: [], 
              txtUrl: null,
              youtubeLinks: [] 
            };
          }

          if (fileName.toLowerCase().endsWith('.txt')) {
            folders[folderName].txtUrl = fileUrl;
          } else {
            const isVideo = /\.(mp4|webm|mov)$/i.test(fileName);
            folders[folderName].mediaFiles.push({
              url: fileUrl,
              fileName: fileName,
              type: isVideo ? 'video' : 'image'
            });
          }
        });

        const loadedEvents = await Promise.all(
          Object.values(folders).map(async (folder, index) => {
            let details = {
              date: '',
              guestCount: '',
              location: '',
              description: '',
              coverFileName: ''
            };

            if (folder.txtUrl) {
              try {
                const response = await fetch(folder.txtUrl);
                const text = await response.text();
                
                text.split('\n').forEach(line => {
                  // CORRECTION IMPORTANTE : Lecture robuste des clés:valeurs
                  const separatorIndex = line.indexOf(':');
                  if (separatorIndex === -1) return;

                  const key = line.substring(0, separatorIndex).trim().toLowerCase();
                  const value = line.substring(separatorIndex + 1).trim(); // Garde tout ce qui est après le premier :

                  if (key === 'date') details.date = value;
                  else if (key.includes('nombre de couvert')) details.guestCount = value;
                  else if (key === 'lieu') details.location = value;
                  else if (key === 'résumé' || key === 'resume') details.description = value;
                  else if (key.includes('photo de couverture')) details.coverFileName = value;
                  else if (key === 'video' || key === 'vidéo') {
                     folder.youtubeLinks.push(value);
                  }
                });
              } catch (e) {
                console.error(`Erreur lecture TXT pour ${folder.name}`, e);
              }
            }

            // --- GESTION INTELLIGENTE DE LA COUVERTURE ---
            let coverImage = null;
            let coverVideoUrl = null;

            // 1. Est-ce que la couverture indiquée est un lien YouTube ?
            const coverIsYouTube = getYouTubeId(details.coverFileName);
            
            if (coverIsYouTube) {
                // C'est un lien YouTube
                coverVideoUrl = details.coverFileName;
                // On génère la miniature YouTube automatiquement comme image de fond
                coverImage = getYouTubeThumbnail(details.coverFileName);
            } else {
                // C'est un fichier local
                let coverMedia = folder.mediaFiles.find(m => m.fileName.toLowerCase() === details.coverFileName.toLowerCase());
                
                // Si pas trouvé, on prend le premier média local par défaut
                if (!coverMedia && folder.mediaFiles.length > 0) coverMedia = folder.mediaFiles[0];

                if (coverMedia) {
                    if (coverMedia.type === 'video') {
                        coverVideoUrl = coverMedia.url;
                        // Pas d'image pour une vidéo locale (sera noir ou première frame)
                    } else {
                        coverImage = coverMedia.url;
                    }
                }
            }

            // Si toujours pas d'image de couverture mais qu'on a des liens YouTube dans la liste
            if (!coverImage && !coverVideoUrl && folder.youtubeLinks.length > 0) {
                // On prend la première vidéo YouTube comme couverture par défaut
                coverVideoUrl = folder.youtubeLinks[0];
                coverImage = getYouTubeThumbnail(folder.youtubeLinks[0]);
            }


            // --- CONSTRUCTION DE LA LISTE DE MÉDIAS ---
            const allMedia = [
                // Fichiers locaux
                ...folder.mediaFiles.map(m => ({
                    type: m.type,
                    url: m.url,
                    fileName: m.fileName
                })),
                // Liens YouTube
                ...folder.youtubeLinks.map(link => ({
                    type: 'video',
                    url: link,
                    isYouTube: true 
                }))
            ];

            return {
              id: index + 100,
              clientName: folder.name,
              date: details.date,
              guestCount: details.guestCount,
              location: details.location,
              description: details.description,
              coverImage: coverImage,
              coverVideo: coverVideoUrl,
              media: allMedia
            };
          })
        );

        setEvents(loadedEvents);
      } catch (err) {
        console.warn("Erreur chargement événements", err);
        setEvents([]); 
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  return { events, loading };
};

const Services = () => {
  const autoGallery = importGalleryImages();
  const displayDishes = autoGallery.length > 0 ? autoGallery : mockData.dishes;

  const { events: autoEvents, loading: eventsLoading } = useEventLoader();
  const displayEvents = autoEvents.length > 0 ? autoEvents : mockData.events;

  return (
    <div className="services-page pt-20">
      <section className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="page-title text-center mb-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold" style={{ color: '#C24D36' }}>
            Services & Réalisations
          </h1>
          <p className="text-xl text-[#F9F6F0] sm:text-2xl md:text-3xl text-center text-[#3E332E] max-w-3xl mx-auto font-bold">
            Nous proposons des solutions de restauration collective végétalienne adaptées à vos besoins, pour 50 à 500 personnes.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12 text-4xl md:text-5xl">Nos Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.services.map((service) => (
              <div key={service.id} className="service-card bg-gray-50 p-8 rounded-lg transition-transform hover:transform hover:-translate-y-2">
                <div className="flex items-center justify-center w-16 h-16 bg-green text-white rounded-full mb-6">
                  <Users size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#C24D36]">{service.title}</h3>
                <p className="text-[#3E332E] mb-4">{service.description}</p>
                <p className="text-sm font-semibold text-black">Capacité : {service.capacity}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-4 text-4xl md:text-5xl" style={{ color: '#C24D36' }}>Nos Événements</h2>
          <p className="text-center text-[#3E332E] mb-12 max-w-4xl mx-auto text-base md:text-lg lg:text-xl">
            Découvrez nos réalisations pour nos clients. Cliquez sur un album pour voir toutes les photos.
          </p>
          {eventsLoading ? (
             <p className="text-center">Chargement des événements...</p>
          ) : (
             <EventAlbums events={displayEvents} />
          )}
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-4 text-4xl md:text-5xl">Nos Créations Culinaires</h2>
          <p className="text-center text-[#3E332E] mb-12 max-w-4xl mx-auto text-base md:text-lg lg:text-xl">
            Une sélection de nos plats végétaliens préparés avec amour et des produits de saison.
          </p>
          {displayDishes.length > 0 ? (
             <DishGallery dishes={displayDishes} />
          ) : (
            <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500 italic mb-2">Aucune image détectée.</p>
              <p className="text-sm text-gray-400">Ajoutez vos photos dans : <code>src/assets/galerie_creations</code></p>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12 text-4xl md:text-5xl" style={{ color: '#C24D36' }}>Témoignages Clients</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card bg-white p-8 rounded-lg">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-[#DDBA6B] fill-current" />
                  ))}
                </div>
                <p className="text-[#3E332E] mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-[#C24D36]">{testimonial.author}</p>
                  <p className="text-sm italic text-[#3E332E]">{testimonial.role}</p>
                  <p className="text-sm font-medium text-[#5A6C45] mt-1">{testimonial.client}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;